import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';

import { modelLabVersionApi } from '../../services/api';
import type { ModelLineage } from '../../types';
import {
  ErrorAlert,
  LoadingSpinner,
  PageHeader,
} from '../common';
import {
  formatMetricSummary,
  getApiErrorMessage,
  modelStageColor,
} from './utils';
import { LineageGraph } from './charts';

const ModelLabVersionsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [lineageVersionId, setLineageVersionId] = useState<number | null>(null);

  const versionsQuery = useQuery({
    queryKey: ['model-lab-versions', search],
    queryFn: () => modelLabVersionApi.list({ page_size: 100, search: search || undefined }),
  });

  const lineageQuery = useQuery({
    queryKey: ['model-lab-lineage', lineageVersionId],
    queryFn: () => modelLabVersionApi.lineage(lineageVersionId!),
    enabled: lineageVersionId != null,
  });

  const promoteMutation = useMutation({
    mutationFn: (id: number) => modelLabVersionApi.promote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-versions'] });
      toast.success('Model version promoted.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to promote model version.'));
    },
  });

  const demoteMutation = useMutation({
    mutationFn: (id: number) => modelLabVersionApi.demote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-versions'] });
      toast.success('Model version demoted.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to demote model version.'));
    },
  });

  const handleDownloadArtifact = async (versionId: number) => {
    try {
      const blob = await modelLabVersionApi.downloadArtifact(versionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `model_version_${versionId}.pkl`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Artifact download started.');
    } catch {
      toast.error('Failed to download artifact.');
    }
  };

  if (versionsQuery.error) {
    return <ErrorAlert error={versionsQuery.error as Error} />;
  }

  if (versionsQuery.isLoading) {
    return <LoadingSpinner message="Loading model versions..." />;
  }

  const versions = versionsQuery.data?.results || [];

  return (
    <Box>
      <PageHeader
        title="Model Versions"
        breadcrumbs={[
          { label: 'Model Lab', to: '/ai/model-lab' },
          { label: 'Model Versions' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Button variant="outlined" onClick={() => navigate('/ai/ml/models')}>
              Models
            </Button>
          </Stack>
        }
      />

      <Stack spacing={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Model Asset</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Dataset Version</TableCell>
                <TableCell>Metrics</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.id} hover>
                  <TableCell>{version.model_asset_name}</TableCell>
                  <TableCell>v{version.version_number}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={version.stage}
                      color={modelStageColor(version.stage)}
                    />
                  </TableCell>
                  <TableCell>{version.dataset_version_name || 'Manual registration'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {formatMetricSummary(version.metrics).map((metric) => (
                        <Chip key={metric} size="small" label={metric} variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open model builder">
                      <IconButton
                        color="secondary"
                        onClick={() => navigate(`/ai/ml/models/${version.model_asset}/edit`)}
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download artifact">
                      <IconButton
                        color="default"
                        onClick={() => handleDownloadArtifact(version.id)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View lineage">
                      <IconButton
                        color="info"
                        onClick={() => setLineageVersionId(version.id)}
                      >
                        <AccountTreeIcon />
                      </IconButton>
                    </Tooltip>
                    {version.stage !== 'PRODUCTION' && (
                      <Tooltip title="Promote">
                        <IconButton
                          color="primary"
                          onClick={() => promoteMutation.mutate(version.id)}
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {version.stage !== 'DRAFT' && (
                      <Tooltip title="Demote">
                        <IconButton
                          color="warning"
                          onClick={() => demoteMutation.mutate(version.id)}
                        >
                          <ArrowDownwardIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {versions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No model versions yet. Training jobs will register them automatically.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog
        open={lineageVersionId != null}
        onClose={() => setLineageVersionId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Model Lineage - Version #{lineageVersionId}</DialogTitle>
        <DialogContent>
          {lineageQuery.isLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {lineageQuery.data && (
            <LineageGraph data={lineageQuery.data as ModelLineage} />
          )}
          {lineageQuery.error && (
            <Alert severity="error">Failed to load lineage data.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineageVersionId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModelLabVersionsPage;
